import { useCallback, useEffect, useState } from "react";
import { KeyRound, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { Panel } from "@/pages/Billing";
import { AssignmentCell, CrmSettingsPanel, ShowroomsPanel } from "@/components/crm/ShowroomsPanel";

const field = "h-11 w-full rounded-xl border border-slate-700 bg-white px-4 text-sm text-slate-100 outline-none transition-colors focus:border-slate-100";
const fmt = (s) => (s ? new Date(s).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "Never");

function AddStaff({ onAdded, rooms }) {
  const [f, setF] = useState({ name: "", email: "", password: "", crm_role: "rep", showroom_id: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) return setError("Name and email are required");
    if (f.password.length < 6) return setError("Temporary password must be at least 6 characters");
    setBusy(true);
    setError("");
    try {
      await api.post("/team", { ...f, name: f.name.trim(), email: f.email.trim(), showroom_id: f.showroom_id || null });
      toast.success(`${f.name.trim()} added. Share the temporary password with them.`);
      setF({ ...f, name: "", email: "", password: "" });
      onAdded();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid gap-3 p-6 md:grid-cols-[1fr_1fr_1fr_auto]" data-testid="add-staff-form">
      <select data-testid="staff-crm-role-select" className={field} value={f.crm_role} onChange={(e) => setF({ ...f, crm_role: e.target.value })}>
        <option value="rep">Sales rep</option><option value="manager">Showroom manager</option>
      </select>
      <select data-testid="staff-showroom-select" className={field} value={f.showroom_id} onChange={(e) => setF({ ...f, showroom_id: e.target.value })}>
        {rooms.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <span className="hidden md:block" /><span className="hidden md:block" />
      <input data-testid="staff-name-input" className={field} placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <input data-testid="staff-email-input" className={field} placeholder="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
      <input data-testid="staff-password-input" className={field} placeholder="Temporary password" type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
      <button data-testid="add-staff-button" disabled={busy}
        className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#0071E3] px-6 text-sm font-semibold text-white transition-colors hover:bg-[#0062CC] disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add staff
      </button>
      {error && <p data-testid="add-staff-error" className="text-sm text-red-300 md:col-span-4">{error}</p>}
    </form>
  );
}

function ResetDialog({ member, onClose }) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const save = async () => {
    if (pw.length < 6) return setError("Password must be at least 6 characters");
    try {
      await api.post(`/team/${member.id}/password`, { password: pw });
      toast.success(`Password reset for ${member.name || member.email}`);
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reset-password-dialog" className="bg-white">
        <DialogHeader><DialogTitle>Reset password for {member?.name || member?.email}</DialogTitle></DialogHeader>
        <input data-testid="reset-password-input" className={field} placeholder="New temporary password" value={pw} onChange={(e) => setPw(e.target.value)} />
        {error && <p data-testid="reset-password-error" className="text-sm text-red-300">{error}</p>}
        <DialogFooter>
          <button data-testid="reset-password-save" onClick={save} className="rounded-full bg-[#0071E3] px-6 py-2.5 text-sm font-semibold text-white hover:bg-black">Set password</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Team() {
  const [team, setTeam] = useState(null);
  const [reset, setReset] = useState(null);
  const [rooms, setRooms] = useState([]);
  const load = useCallback(() => api.get("/team").then((r) => setTeam(r.data)).catch((e) => toast.error(errorMessage(e))), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (m) => {
    try {
      await api.post(`/team/${m.id}/status`, { active: !m.active });
      toast.success(m.active ? `${m.name || m.email} deactivated` : `${m.name || m.email} reactivated`);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <div className="space-y-10" data-testid="team-page">
      <div className="rise">
        <h1 className="font-display text-4xl font-semibold tracking-tight text-slate-50 sm:text-5xl">Team</h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-400 md:text-base">Staff share your company credits and can read drawings, build quotes and download PDFs. They only ever see suggested and final prices, never your costs or multiplier.</p>
      </div>
      <ShowroomsPanel onChanged={setRooms} />
      <Panel title="Add a staff member" testId="add-staff-panel"><AddStaff onAdded={load} rooms={rooms} /></Panel>
      <Panel title="Your team" testId="team-list-panel">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]" data-testid="team-table">
            <thead><tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-6 py-3">Name</th><th className="px-6 py-3">Email</th><th className="px-6 py-3">Role</th>
              <th className="px-6 py-3">Status</th><th className="px-6 py-3">Last login</th><th className="px-6 py-3 text-right">Actions</th></tr></thead>
            <tbody>{team?.map((m, i) => (
              <tr key={m.id} data-testid={`team-row-${i}`} className="border-t border-slate-800/70 text-sm">
                <td className="px-6 py-4 font-medium text-slate-100" data-testid={`team-name-${i}`}>{m.name || "—"}</td>
                <td className="px-6 py-4 text-slate-400" data-testid={`team-email-${i}`}>{m.email}</td>
                <td className="px-6 py-4 text-slate-300" data-testid={`team-role-${i}`}><AssignmentCell m={m} i={i} rooms={rooms} onSaved={load} /></td>
                <td className="px-6 py-4">
                  <span data-testid={`team-status-${i}`} className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${m.active ? "border-emerald-500/40 text-emerald-300" : "border-slate-700 text-slate-500"}`}>{m.active ? "active" : "inactive"}</span>
                </td>
                <td className="px-6 py-4 text-slate-400" data-testid={`team-last-login-${i}`}>{fmt(m.last_login)}</td>
                <td className="px-6 py-4 text-right">
                  {m.role === "staff" && (
                    <div className="flex justify-end gap-2">
                      <button data-testid={`team-reset-button-${i}`} onClick={() => setReset(m)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-slate-100"><KeyRound className="h-3.5 w-3.5" /> Reset password</button>
                      <button data-testid={`team-toggle-button-${i}`} onClick={() => toggle(m)}
                        className={`rounded-full px-4 py-1.5 text-xs font-semibold ${m.active ? "bg-red-500/10 text-red-400 hover:bg-red-500/15" : "bg-[#0071E3] text-white hover:bg-[#0062CC]"}`}>{m.active ? "Deactivate" : "Reactivate"}</button>
                    </div>
                  )}
                </td>
              </tr>))}
            </tbody>
          </table>
          {team === null && <div className="h-24 animate-pulse bg-slate-900/40" />}
        </div>
      </Panel>
      <CrmSettingsPanel />
      <ResetDialog member={reset} onClose={() => setReset(null)} />
    </div>
  );
}
